import React, { useState, useEffect, useRef } from 'react';
import { getStartOfWeek } from "./components/Utils";
import Tooltip from "../../common/components/Tooltip";

const generateCalendarDays = (year, month) => {
  const days = [];
  const date = new Date(year, month, 1);
  // Generate days for the current month
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }

  // Add days from the previous month to start the week on Sunday
  let firstDayOfWeek = days[0].getDay(); // 0 is Sunday
  if (firstDayOfWeek !== 1) {
    firstDayOfWeek += firstDayOfWeek === 0 ? 7 : 0;
    const prevMonth = new Date(year, month, 0); // Last day of the previous month
    for (let i = 0; i < firstDayOfWeek - 1; i++) {
      const additionalDay = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), prevMonth.getDate() - i);
      days.unshift(additionalDay);
    }
  }

  // Add days from the next month to end the week on Saturday
  const lastDayOfWeek = days[days.length - 1].getDay(); // 6 is Saturday
  if (lastDayOfWeek !== 0) {
    const nextMonth = new Date(year, month + 1, 1); // First day of the next month
    for (let i = 1; i <= 7 - lastDayOfWeek; i++) {
      const additionalDay = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), nextMonth.getDate() + i - 1);
      days.push(additionalDay);
    }
  }

  return days;
};

const MonthSelect = ({ handleChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(getStartOfWeek(new Date()));
  const calendarRef = useRef(null); // Reference to the calendar container for outside-click detection

  // Function to toggle calendar popup
  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };
  const selectDate = date => {
    const newDate = getStartOfWeek(date);
    setCurrentDate(newDate);
    handleChange(newDate);
    setIsOpen(false);
  };

  const goToNextWeek = (applyDate = true) => {
    const day = new Date(currentDate);
    day.setDate(day.getDate() + 7);
    if (applyDate) {
      selectDate(day);
    } else {
      setCurrentDate(day);
    }
  };

  const goToPrevWeek = (applyDate = true) => {
    const day = new Date(currentDate);
    day.setDate(day.getDate() - 7);
    if (applyDate) {
      selectDate(day);
    } else {
      setCurrentDate(day);
    }
  };


  const goToNextMonth = () => {
    const day = new Date(currentDate);
    day.setDate(1);
    day.setMonth(day.getMonth() + 1);
    setCurrentDate(day);
  };

  const goToPrevMonth = () => {
    const day = new Date(currentDate);
    day.setDate(1);
    day.setMonth(day.getMonth() - 1);
    setCurrentDate(day);
  };

  const gotToCurrentWeek = () => selectDate(new Date());

  const handleClickOutside = event => {
    if (calendarRef.current && !calendarRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const monthDays = generateCalendarDays(currentDate.getFullYear(), currentDate.getMonth());

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric', day: '2-digit' });

  return (
    <div className="monthSelect" style={{ position: 'relative', display: 'inline-block' }} ref={calendarRef}>
      {/* Main Button */}
        <button onClick={gotToCurrentWeek} className="btn -empty -y2023">Current week</button>
      <button onClick={toggleCalendar} className="btn -white -min-width -y2023">{monthName}</button>
      <Tooltip tooltip='Previous Week' data-tooltip-left={1}>
        <button onClick={goToPrevWeek} className="btn -white -y2023">‹‹</button>
      </Tooltip>
      <Tooltip tooltip='Next Week' data-tooltip-left={1}>
        <button onClick={goToNextWeek} className="btn -white -y2023">››</button>
      </Tooltip>
      {/* Calendar Popup */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            padding: '10px',
            borderRadius: '4px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={() => goToPrevMonth(false)}>{'<'}</button>
            <span>{monthName}</span>
            <button onClick={() => goToNextMonth(false)}>{'>'}</button>
          </div>

          {/* Calendar Days */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '5px',
              marginTop: '10px',
            }}
          >
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} style={{ fontWeight: 'bold', textAlign: 'center' }}>
                {day}
              </div>
            ))}

            {monthDays.map((day, index) => {
              const bg = day.getMonth() === currentDate.getMonth() ? '#f0f0f0' : '#cccccc';
              return (
                <button
                  key={index}
                  onClick={() => selectDate(day)}
                  style={{
                    padding: '8px',
                    textAlign: 'center',
                    backgroundColor: bg,
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {day.getDate()}
                </button>
            )})}
          </div>

          {/* Today Button */}
          <button
            onClick={gotToCurrentWeek}
            style={{
              marginTop: '10px',
              width: '100%',
              padding: '8px',
              backgroundColor: '#142640',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Today
          </button>
        </div>
      )}
    </div>
  );
};

export default MonthSelect;



// WEBPACK FOOTER //
// ./app/javascript/components/designer_calendars/MonthSelect.js